import React, { useEffect, useState } from "react";
import axios from "axios";
import { withTranslation } from "react-i18next";
import { FaInfoCircle } from "react-icons/fa";

import myConfig from "../../configs/config";
import SidebarToolInfoModal from "../../components/SidebarToolInfoModal";
import PlanOperatorSelectionModal from "./PlanOperatorSelectionModal";
import {
  OPERATOR_PLAN_HELP_TITLE,
  OperatorPlanHelpContent,
} from "./operatorPlanHelpContent";
import "./OperatorPlanQuotaBar.css";

function OperatorPlanQuotaBar({ farmId, t, onUpdated, refreshKey }) {
  const [planStatus, setPlanStatus] = useState(null);
  const [operators, setOperators] = useState([]);
  const [planLimit, setPlanLimit] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  const [showSelection, setShowSelection] = useState(false);

  async function loadPlanOperators() {
    if (!farmId) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${myConfig.API_URL}/farm/${farmId}/plan-operators/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("client-token")}`,
          },
        }
      );
      setPlanStatus(response.data.plan_operator_status || null);
      setPlanLimit(response.data.plan_free_operators || 0);
      setOperators(Array.isArray(response.data.operators) ? response.data.operators : []);
    } catch (error) {
      setPlanStatus(null);
      setOperators([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPlanOperators();
  }, [farmId, refreshKey]);

  if (loading || !planStatus || planLimit <= 0) {
    return null;
  }

  const needsSelection = planStatus.needs_selection;
  const statusClass = needsSelection
    ? "operator-plan-quota-bar--warning"
    : planStatus.available_slots === 0
      ? "operator-plan-quota-bar--full"
      : "operator-plan-quota-bar--ok";

  async function handleSelectionSaved(includedOperatorIds) {
    await axios.post(
      `${myConfig.API_URL}/farm/${farmId}/plan-operators/`,
      {
        plan_free_operators: planLimit,
        included_operator_ids: includedOperatorIds,
      },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
        },
      }
    );
    await loadPlanOperators();
    if (onUpdated) onUpdated();
  }

  return (
    <>
      <div className={`operator-plan-quota-bar ${statusClass}`}>
        <div className="operator-plan-quota-bar__main">
          <strong>{t("Operadores do plano")}</strong>
          <span className="operator-plan-quota-bar__count">
            {t("{{used}} de {{limit}} vagas gratuitas", {
              used: planStatus.included_count,
              limit: planStatus.limit,
            })}
          </span>
          {planStatus.extra_operators > 0 ? (
            <span className="operator-plan-quota-bar__extra">
              +{planStatus.extra_operators} {t("adicionais")}
            </span>
          ) : null}
          {planStatus.available_slots > 0 ? (
            <span className="operator-plan-quota-bar__available">
              {t("{{count}} vaga(s) disponível(is)", {
                count: planStatus.available_slots,
              })}
            </span>
          ) : null}
        </div>
        <div className="operator-plan-quota-bar__actions">
          <button
            type="button"
            className="operator-plan-quota-bar__btn"
            onClick={() => setShowSelection(true)}
          >
            {needsSelection
              ? t("Escolher operadores do plano")
              : t("Gerenciar vagas do plano")}
          </button>
          <button
            type="button"
            className="operator-plan-quota-bar__info-btn"
            title={t("Como funcionam os operadores do plano?")}
            aria-label={t("Como funcionam os operadores do plano?")}
            onClick={() => setShowHelp(true)}
          >
            <FaInfoCircle />
          </button>
        </div>
      </div>

      {needsSelection ? (
        <div className="alert alert-warning operator-plan-quota-bar__alert">
          {t(
            "O plano atual tem menos vagas gratuitas do que operadores vinculados. Escolha quais permanecem no plano."
          )}
        </div>
      ) : null}

      {showSelection ? (
        <PlanOperatorSelectionModal
          operators={operators}
          limit={planLimit}
          title={t("Escolher operadores do plano")}
          description={t(
            "Selecione até {{limit}} operador(es) que usarão as vagas gratuitas do plano. Os demais ficam como adicionais.",
            { limit: planLimit }
          )}
          onClose={() => setShowSelection(false)}
          onSave={handleSelectionSaved}
        />
      ) : null}

      {showHelp ? (
        <SidebarToolInfoModal
          title={t(OPERATOR_PLAN_HELP_TITLE)}
          onClose={() => setShowHelp(false)}
        >
          <OperatorPlanHelpContent planStatus={planStatus} />
        </SidebarToolInfoModal>
      ) : null}
    </>
  );
}

export default withTranslation()(OperatorPlanQuotaBar);
